import { Favorite, Home, Search } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useSearchDialog } from '@tunasong/search'
import { ShortcutBox, UserAvatar, VBox, shortcuts, useFavorites, useHover, useNavigate, Typography, useTunaMediaQuery } from '@tunasong/ui-lib'
import type { CSSProperties } from 'react'
import { useRef, type FC } from 'react'
import { ToolsMenuItems } from './tools-menu-items.js'

export interface AppSideBarProps {
  entity?: Persisted<Entity>
}

export const AppSideBar: FC<AppSideBarProps> = props => {
  const { entity } = props

  const { favorites } = useFavorites()

  const { userId } = useCurrentUser()

  const isSmall = useTunaMediaQuery('(max-width: 600px)')
  const avatarSize = isSmall ? 'tiny' : 'small'

  const { dialog: searchDialog, setOpen: setSearchOpen, open: searchOpen } = useSearchDialog(entity)

  const navigate = useNavigate()
  const menuItemStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: 'inherit',
    width: '100%',
    textAlign: 'left',
    fontSize: 14,
  }

  const ref = useRef<HTMLDivElement>(null)
  const hover = useHover(ref.current)

  // const handleLayout = (layoutName: string) => (ev: MouseEvent) => {
  //   ev.preventDefault()
  //   setLayout(layoutName)
  // }

  return (
    <VBox
      ref={ref}
      style={{
        overflow: 'auto',
        marginTop: 8,
        backgroundColor: 'var(--tuna-palette-layers)',
        borderRight: 'solid 1px var(--tuna-palette-divider)',
        color: 'var(--tuna-palette-action-active)',
        transition: 'all 0.25s',
        zIndex: 1,
        width: 192,
      }}
    >
      <div style={{ marginLeft: 16, display: 'flex' }}>
        <UserAvatar size={avatarSize} userId={userId} aria-label="user" showName={true} />
      </div>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <button
          onClick={() => navigate('/app')}
          style={{
            ...menuItemStyle,
            backgroundColor: location.pathname === '/app' ? 'var(--tuna-palette-action-selected)' : undefined,
          }}
        >
          <Home />
          <span>Home</span>
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          style={{
            ...menuItemStyle,
            backgroundColor: searchOpen ? 'var(--tuna-palette-action-selected)' : undefined,
          }}
        >
          <Search />
          <span style={{ flex: 1 }}>Search</span>
          <ShortcutBox shortcut={shortcuts.Search} showDescription={false} />
        </button>

        <div style={{ ...menuItemStyle, opacity: 0.5, cursor: 'default' }}>
          <Typography variant="caption">Tools</Typography>
        </div>

        <ToolsMenuItems variant="caption" />

        <div style={{ flex: 1 }} />

        <div style={{ ...menuItemStyle, opacity: 0.5, cursor: 'default' }}>
          <Favorite size={16} />
          <Typography variant="caption">Favorites</Typography>
        </div>

        {favorites.map(favorite => (
          <button key={favorite.path} style={menuItemStyle} onClick={() => navigate(favorite.path)}>
            <span style={{ color: hover ? favorite.color : 'var(--tuna-palette-action-disabled)' }}>
              <favorite.Icon />
            </span>
            <Typography variant="caption">{favorite.name}</Typography>
          </button>
        ))}
        {favorites.length === 0 ? (
          <div style={{ ...menuItemStyle, opacity: 0.5, cursor: 'default' }}>
            <Typography variant="caption">No favorites</Typography>
          </div>
        ) : null}
      </div>

      {searchDialog}
    </VBox>
  )
}
